import React, { useState, useMemo, useCallback } from 'react';
import GoogleMapReact from 'google-map-react';

import { Grid, Typography, Paper, Modal, Box } from '@mui/material';
import { Button } from '@material-ui/core';
import IconButton from '@mui/material/IconButton';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import CloseIcon from '@mui/icons-material/Close';
import NotListedLocationIcon from '@mui/icons-material/NotListedLocation';
import PinDropIcon from '@mui/icons-material/PinDrop';

import {
  COLOR_PRIMARY_LIGHT,
  COLOR_PRIMARY_DARK,
  BACKGROUND_COLOR,
  COLOR_ACCENT_1,
  mapOptions,
} from 'src/const';
import { formatCurrency } from 'src/utils/functions';
import { CustomCircleBoundaryModalPropTypes } from 'src/interfaces/iproptypes';

const RIPPLE_SIZE = 100;
const RIPPLE_COLOR = COLOR_ACCENT_1;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    options: {
      '&:hover': {
        backgroundColor: COLOR_PRIMARY_LIGHT,
        fontColor: '#abcdef',
        textColor: '#abcdef',
      },
    },
    button: {
      background: COLOR_PRIMARY_DARK,
      borderRadius: 5,
      color: 'white',
      padding: '5px 30px',
      boxShadow: '0 1px 1px 1px rgba(255, 255, 255, .3)',
      '&:hover': {
        background: COLOR_PRIMARY_LIGHT,
      },
      fontSize: 16,
    },
    selectLoactionButton: {
      [theme.breakpoints.down(900)]: {
        position: 'absolute',
        bottom: 25,
        width: '80%',
        left: '10%',
        right: '10%',
        borderRadius: 100,
        boxSizing: 'border-box',
      },
    },
    iconButton: {
      [theme.breakpoints.down(900)]: {
        position: 'relative',
        zIndex: 100,
        background: '#fff !important',
        borderRadius: '50%',
        height: 70,
        width: 70,
        boxShadow: '0px -2px 7px -1px rgba(0, 0, 0, 0.55)',
        padding: 10,
        color: '#000',
        transform: 'scale(0.7) translateY(-20px)',
      },
    },
    customLocationMapContainer: {
      height: 400,
      [theme.breakpoints.down(900)]: {
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
      },
    },
    paperModal: {
      backgroundColor: BACKGROUND_COLOR,
      width: 600,
      padding: theme.spacing(2),
      animation: 'fade-scale-in 0.3s ease forwards',
      //tablet, mobile
      [theme.breakpoints.down(900)]: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100vh',
        boxSizing: 'border-box',
      },
    },
    ripple: {
      position: 'absolute',
      top: 0,
      left: 0,
      marginTop: -RIPPLE_SIZE / 2,
      marginLeft: -RIPPLE_SIZE / 2,
      width: RIPPLE_SIZE,
      height: RIPPLE_SIZE,
      animation: `$rippleAnimation 1000ms ${theme.transitions.easing.easeOut}`,
      animationIterationCount: 'infinite',
      opacity: 0,
      borderRadius: '50%',
      boxShadow: `0 0 6px 3px ${RIPPLE_COLOR}`,
    },
    '@keyframes rippleAnimation': {
      '0%': {
        transform: 'scale(0.1, 0.1)',
        opacity: 0,
      },
      '1%': {
        opacity: 1,
      },
      '50% ': {
        opacity: 1,
      },
      '100%': {
        transform: 'scale(1.2, 1.2)',
        opacity: 0,
      },
    },
  })
);

const CustomCircleBoundaryModal = ({
  matches,
  customCircleBoundaryModalOpen,
  setCustomCircleBoundaryModalOpen,
  centerLat,
  centerLng,
  setCenterLat,
  setCenterLng,
  radiusInMeter,
  setRadiusInMeter,
  infoText,
  setLocation,
  minRadiusInMeter,
  maxRadiusInMeter,
}: CustomCircleBoundaryModalPropTypes) => {
  const classes = useStyles();

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(true);
  const [circleCopy, setCircleCopy] = useState<any>(null);

  const locationPin = useMemo(() => {
    return (
      <div>
        <div className={classes.ripple}></div>
        <PinDropIcon
          style={{
            fontSize: 40,
            position: 'absolute',
            top: -20,
            left: -20,
          }}
        />
      </div>
    );
  }, [centerLat, centerLng]);

  const handleGoogleMapAPIOnLoad = (map, maps) => {
    const drawingManager = new maps.drawing.DrawingManager({
      drawingControl: false,
      circleOptions: {
        editable: true,
        draggable: false,
        clickble: false,
      },
    });

    drawingManager.setMap(map);

    const circle: any = new google.maps.Circle({
      strokeColor: '#000022',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#000022',
      fillOpacity: 0.35,
      map: map,
      radius: radiusInMeter,
      center: map.getCenter(),
      editable: true,
    });

    setCircleCopy(circle);

    map.addListener('dragend', () => {
      circle.setCenter(map.getCenter());
    });

    circle.addListener('radius_changed', () => {
      if (circle.radius < minRadiusInMeter) {
        circle.setRadius(minRadiusInMeter);
      } else if (circle.radius > maxRadiusInMeter) {
        circle.setRadius(maxRadiusInMeter);
      }
      setRadiusInMeter(Math.round(circle.radius));
    });
  };

  const handleMapChange = useCallback(({ center }) => {
    const centerLat: number = center.lat();
    const centerLng: number = center.lng();
    setCenterLat(centerLat);
    setCenterLng(centerLng);
  }, []);

  const confirmCircleBoundary = () => {
    setLocation();
    setCustomCircleBoundaryModalOpen(false);
  };

  return (
    <>
      <Modal
        open={customCircleBoundaryModalOpen}
        onClose={() => setCustomCircleBoundaryModalOpen(false)}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{ minHeight: '100vh' }}
        >
          <div className={`${classes.paperModal} modal-custom`}>
            <Grid
              container
              direction={matches ? 'column' : 'row'}
              justifyContent="flex-end"
              alignItems="flex-end"
            >
              <IconButton
                className={classes.iconButton}
                onClick={() => setCustomCircleBoundaryModalOpen(false)}
              >
                <CloseIcon sx={{ color: '#f00', fontSize: 40 }} />
              </IconButton>
              <IconButton className={classes.iconButton} onClick={() => setIsInfoModalOpen(true)}>
                <NotListedLocationIcon sx={{ color: '#000', fontSize: 40 }} />
              </IconButton>
            </Grid>
            <Paper className={classes.customLocationMapContainer}>
              <GoogleMapReact
                center={{ lat: centerLat, lng: centerLng }}
                options={{
                  ...mapOptions,
                  fullscreenControl: matches ? false : true,
                  zoomControlOptions: { position: matches ? 4 : 9 },
                }}
                zoom={8}
                resetBoundsOnResize
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) => handleGoogleMapAPIOnLoad(map, maps)}
                onDragEnd={map => handleMapChange(map)}
              >
                {!circleCopy && locationPin}
              </GoogleMapReact>
            </Paper>

            <Grid
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ marginTop: '10px' }}
            >
              <Typography sx={{ fontWeight: 500 }}>
                Radius:{' '}
                <span style={{ color: COLOR_PRIMARY_DARK }}>
                  {formatCurrency(Math.round(radiusInMeter / 1000).toString())} km
                </span>
              </Typography>

              {matches && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '100%',
                    height: '100px',
                    background: '#fff',
                    borderRadius: '50px 50px 0 0',
                    boxShadow: '0px -2px 7px -1px rgba(0, 0, 0, 0.25)',
                  }}
                ></div>
              )}

              <Button
                size="small"
                variant="outlined"
                className={`${classes.button} ${classes.selectLoactionButton}`}
                onClick={confirmCircleBoundary}
              >
                Discover Markets
              </Button>
            </Grid>
          </div>
        </Grid>
      </Modal>

      {isInfoModalOpen && customCircleBoundaryModalOpen && (
        <Modal
          open={isInfoModalOpen}
          onClose={() => setIsInfoModalOpen(false)}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box
            sx={{
              width: '70vw',
              maxWidth: '400px',
              bgcolor: COLOR_PRIMARY_LIGHT,
              boxShadow: 24,
              p: 4,
              borderRadius: '20px',
              animation: 'fade-scale-in 0.3s ease forwards',
            }}
          >
            <Typography sx={{ color: '#000' }}>{infoText}</Typography>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                setIsInfoModalOpen(false);
              }}
              style={{
                background: '#fff',
                color: '#000',
                borderRadius: 100,
                padding: '5px auto',
                outline: 'none',
                border: 'none',
                textTransform: 'capitalize',
                marginTop: 16,
                width: '100%',
                fontSize: '18px',
                fontWeight: 'bold',
              }}
            >
              I Understood
            </Button>
          </Box>
        </Modal>
      )}
    </>
  );
};

export default CustomCircleBoundaryModal;
